// Financial Events and Tracking Utilities

/**
 * Calculate debt payoff schedule using amortization
 */
export const calculateDebtPayoff = (principal, annualRate, monthlyPayment) => {
  if (monthlyPayment <= 0 || principal <= 0) return [];

  const monthlyRate = annualRate / 12;
  let balance = principal;
  const schedule = [];
  let month = 0;
  const maxMonths = 600; // 50 years max

  while (balance > 0.01 && month < maxMonths) {
    const interestPayment = balance * monthlyRate;
    const principalPayment = Math.min(monthlyPayment - interestPayment, balance);
    balance -= principalPayment;

    schedule.push({
      month: month + 1,
      payment: monthlyPayment,
      principal: principalPayment,
      interest: interestPayment,
      balance: Math.max(0, balance)
    });

    month++;

    // If balance is very small, pay it off
    if (balance < monthlyPayment && balance > 0) {
      balance = 0;
    }
  }

  return schedule;
};

/**
 * Calculate total interest paid on debt
 */
export const calculateTotalInterest = (principal, annualRate, monthlyPayment) => {
  const schedule = calculateDebtPayoff(principal, annualRate, monthlyPayment);
  return schedule.reduce((sum, payment) => sum + payment.interest, 0);
};

/**
 * Calculate minimum payment for credit card debt (% of balance)
 */
export const calculateCreditCardMinPayment = (balance, minRate = 0.02) => {
  return Math.max(balance * minRate, 25); // Min $25 or 2% of balance
};

/**
 * Get debt status at a specific age
 */
export const getDebtStatusAtAge = (debt, currentAge, targetAge) => {
  const yearsElapsed = targetAge - currentAge;
  const monthsElapsed = yearsElapsed * 12;

  const schedule = calculateDebtPayoff(debt.balance, debt.interestRate, debt.monthlyPayment);

  if (monthsElapsed >= schedule.length) {
    return { balance: 0, isPaidOff: true, monthlyPayment: 0 };
  }

  const payment = schedule[monthsElapsed] || schedule[schedule.length - 1];
  return {
    balance: payment.balance,
    isPaidOff: payment.balance <= 0,
    monthlyPayment: debt.monthlyPayment
  };
};

/**
 * Calculate 529 college savings growth
 */
export const calculate529Growth = (monthlyContribution, years, annualReturn = 0.07) => {
  const months = years * 12;
  const monthlyRate = annualReturn / 12;
  let balance = 0;

  for (let i = 0; i < months; i++) {
    balance = balance * (1 + monthlyRate) + monthlyContribution;
  }

  return balance;
};

/**
 * Estimate college costs (4 years, adjusted for inflation)
 */
export const estimateCollegeCosts = (yearsUntilCollege, costPerYear = 30000, inflationRate = 0.05) => {
  // Cost when child starts college
  const startCost = costPerYear * Math.pow(1 + inflationRate, yearsUntilCollege);

  // Total for 4 years with continuing inflation
  let totalCost = 0;
  for (let year = 0; year < 4; year++) {
    totalCost += startCost * Math.pow(1 + inflationRate, year);
  }

  return {
    totalCost,
    firstYearCost: startCost,
    averageYearCost: totalCost / 4
  };
};

/**
 * Calculate if college savings will cover costs
 */
export const analyzeCollegeFunding = (
  monthlyContribution,
  yearsUntilCollege,
  currentSavings = 0,
  costPerYear = 30000
) => {
  const projected529Balance = calculate529Growth(monthlyContribution, yearsUntilCollege) + currentSavings;
  const collegeCosts = estimateCollegeCosts(yearsUntilCollege, costPerYear);

  return {
    projected529Balance,
    totalCollegeCost: collegeCosts.totalCost,
    surplus: projected529Balance - collegeCosts.totalCost,
    percentageCovered: (projected529Balance / collegeCosts.totalCost) * 100,
    shortfall: Math.max(0, collegeCosts.totalCost - projected529Balance)
  };
};

/**
 * Event Types
 */
export const EVENT_TYPES = {
  // Income events
  WINDFALL: 'windfall',
  INHERITANCE: 'inheritance',
  BONUS: 'bonus',
  SALARY_INCREASE: 'salary_increase',
  JOB_CHANGE: 'job_change',
  PROMOTION: 'promotion',
  LAYOFF: 'layoff',
  CAREER_BREAK: 'career_break',

  // Expense events
  MAJOR_PURCHASE: 'major_purchase',
  HOME_PURCHASE: 'home_purchase',
  CAR_PURCHASE: 'car_purchase',
  WEDDING: 'wedding',
  HOME_RENOVATION: 'home_renovation',
  MEDICAL_EXPENSE: 'medical_expense',
  EMERGENCY: 'emergency',

  // Debt events
  DEBT_PAYOFF: 'debt_payoff',
  NEW_DEBT: 'new_debt',

  // Life events
  CHILD_BIRTH: 'child_birth',
  MARRIAGE: 'marriage',
  DIVORCE: 'divorce',
  RETIREMENT: 'retirement'
};

/**
 * Process one-time event at a specific year
 */
export const processEvent = (event, yearData) => {
  switch (event.type) {
    case EVENT_TYPES.WINDFALL:
    case EVENT_TYPES.INHERITANCE:
    case EVENT_TYPES.BONUS:
      return { ...yearData, cashFlow: yearData.cashFlow + event.amount };

    case EVENT_TYPES.MAJOR_PURCHASE:
    case EVENT_TYPES.CAR_PURCHASE:
    case EVENT_TYPES.WEDDING:
    case EVENT_TYPES.HOME_RENOVATION:
    case EVENT_TYPES.MEDICAL_EXPENSE:
      return { ...yearData, cashFlow: yearData.cashFlow - event.amount };

    case EVENT_TYPES.SALARY_INCREASE:
      return { ...yearData, income: yearData.income * (1 + event.percentChange) };

    case EVENT_TYPES.JOB_CHANGE:
    case EVENT_TYPES.PROMOTION:
      return { ...yearData, income: event.newIncome };

    case EVENT_TYPES.LAYOFF:
    case EVENT_TYPES.CAREER_BREAK:
      return { ...yearData, income: event.unemploymentIncome || 0 };

    default:
      return yearData;
  }
};

/**
 * Standard major purchase costs (in today's dollars)
 */
export const STANDARD_PURCHASE_COSTS = {
  car_new: { min: 30000, avg: 40000, max: 60000 },
  car_used: { min: 15000, avg: 25000, max: 35000 },
  wedding_small: { min: 10000, avg: 15000, max: 20000 },
  wedding_medium: { min: 20000, avg: 30000, max: 40000 },
  wedding_large: { min: 40000, avg: 60000, max: 100000 },
  home_renovation: { min: 20000, avg: 50000, max: 100000 },
  boat: { min: 25000, avg: 50000, max: 150000 },
  rv: { min: 50000, avg: 100000, max: 300000 },
  vacation: { min: 3000, avg: 7000, max: 15000 }
};

/**
 * Debt Types with typical rates
 */
export const DEBT_TYPES = {
  mortgage: { typical_rate: 0.07, typical_term: 30 },
  student_loan: { typical_rate: 0.06, typical_term: 10 },
  auto_loan: { typical_rate: 0.065, typical_term: 5 },
  credit_card: { typical_rate: 0.20, typical_term: null },
  personal_loan: { typical_rate: 0.11, typical_term: 5 },
  heloc: { typical_rate: 0.08, typical_term: 10 }
};
